import { Building2, Target, Users, MapPin, ShieldCheck, BadgeCheck, IndianRupee, Star, Heart, Award, TrendingDown, Lock } from 'lucide-react';

const values = [
  { icon: ShieldCheck, color: 'emerald', title: 'Absolute Transparency', desc: 'Every fee, every clause, every term is explained to you in plain language before you commit to anything.' },
  { icon: TrendingDown, color: 'sky', title: 'Lowest Cost Commitment', desc: 'We benchmark your offer against the entire market. If any partner bank can do better, we find them.' },
  { icon: Lock, color: 'violet', title: 'Data Privacy First', desc: 'Your PAN, Aadhaar and income data is encrypted and accessed only by RBI-licensed partners — never sold or shared.' },
  { icon: Heart, color: 'rose', title: 'Customer-First Always', desc: 'Our advisors earn the same commission regardless of which bank they recommend — so advice is always unbiased.' },
];

const milestones = [
  { year: '2020', event: 'Founded in Mumbai with 5 bank partners and a vision for transparent lending.' },
  { year: '2021', event: 'Expanded to 30 cities, onboarded 100+ certified agents and crossed ₹100Cr disbursed.' },
  { year: '2022', event: 'Launched AI-powered rate comparison engine covering 50+ lenders across all loan categories.' },
  { year: '2023', event: 'Reached ₹500Cr+ total disbursements, 10,000+ happy borrowers, and 500+ agent partners.' },
  { year: '2024', event: 'Introduced real-time EMI calculator, document vault, and dedicated grievance resolution desk.' },
];

const whyTrustUs = [
  { stat: '0', label: 'Hidden Fees', sub: 'What we show is what you pay' },
  { stat: '48h', label: 'Avg. Approval Time', sub: 'From application to sanction' },
  { stat: '50+', label: 'Lenders Compared', sub: 'For every single application' },
  { stat: '98%', label: 'Satisfaction Rate', sub: 'Based on post-disbursal feedback' },
];

const About = () => {
  return (
    <div className="min-h-screen bg-background pb-24">

      {/* ── Hero ── */}
      <div className="bg-slate-900 text-white py-20 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-950 opacity-80" />
        <div className="relative z-10 container mx-auto px-4">
          <span className="inline-block bg-emerald-500/15 text-emerald-400 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">Est. 2020 · Mumbai</span>
          <h1 className="text-4xl md:text-5xl font-bold font-['Plus_Jakarta_Sans'] mb-4">About Us</h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Empowering millions with accessible financial solutions. We bridge the gap between financial institutions and borrowers — honestly, transparently, and always on your side.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-6xl">

        {/* ── Mission ── */}
        <div className="grid md:grid-cols-2 gap-12 items-center my-24">
          <div>
            <span className="inline-block bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">Our Mission</span>
            <h2 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] mb-6">Making loans simple, fair, and completely safe</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              Founded in 2020, FinMatrixRQ has grown to become India's leading digital Direct Selling Agent platform. We are committed to making financial products accessible, transparent, and hassle-free for everyone — from first-time borrowers to seasoned business owners.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              Our core belief: <strong className="text-slate-900 dark:text-white">you should never pay a rupee more than necessary</strong>. We guide you through every step to avoid extra costs, choose the right lender, and make payments safely.
            </p>
            <div className="flex items-center gap-4 text-emerald-600 dark:text-emerald-400 font-semibold text-lg">
              <Target className="h-6 w-6" /> Over ₹500Cr+ Disbursed · Zero hidden charges
            </div>
          </div>
          <div className="bg-slate-100 dark:bg-slate-800 rounded-3xl p-8 aspect-square flex items-center justify-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-blue-500/20"></div>
            <Building2 className="w-48 h-48 text-emerald-500 opacity-80" />
          </div>
        </div>

        {/* ── Stats ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-24">
          {[
            { tag: '50+', label: 'Partner Banks', icon: Building2 },
            { tag: '10K+', label: 'Happy Clients', icon: Users },
            { tag: '100+', label: 'Cities Covered', icon: MapPin },
            { tag: '500+', label: 'Certified Agents', icon: Users }
          ].map((stat, i) => (
            <div key={i} className="text-center p-6 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 hover:shadow-md transition-shadow">
              <stat.icon className="w-8 h-8 mx-auto mb-4 text-emerald-500" />
              <h3 className="text-3xl font-bold mb-2">{stat.tag}</h3>
              <p className="text-muted-foreground font-medium">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* ── NEW: Why You Can Trust Us (trust metrics) ── */}
        <div className="mb-24">
          <div className="rounded-3xl bg-gradient-to-br from-emerald-600 to-teal-700 p-10 md:p-16 text-white overflow-hidden relative">
            <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-white/5 rounded-full" />
            <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-64 h-64 bg-white/5 rounded-full" />
            <div className="relative z-10 text-center mb-12">
              <h2 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] mb-4">Numbers that prove our commitment</h2>
              <p className="text-emerald-100 max-w-xl mx-auto">We don't just claim to be the best — our results speak for themselves.</p>
            </div>
            <div className="relative z-10 grid grid-cols-2 md:grid-cols-4 gap-8 divide-x divide-white/20">
              {whyTrustUs.map((w, i) => (
                <div key={i} className="text-center px-4">
                  <p className="text-4xl md:text-5xl font-black text-white mb-1">{w.stat}</p>
                  <p className="font-bold text-emerald-100 text-sm">{w.label}</p>
                  <p className="text-emerald-200/70 text-xs mt-1">{w.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── NEW: Our Core Values ── */}
        <div className="mb-24">
          <div className="text-center mb-12">
            <span className="inline-block bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">What We Stand For</span>
            <h2 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] mb-4">Our values protect you at every stage</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">These aren't just words on a wall — they're how every FinMatrixRQ advisor is trained to behave.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <div key={i} className="group p-7 rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-800 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className={`h-12 w-12 rounded-2xl flex items-center justify-center mb-5 bg-${v.color}-100 dark:bg-${v.color}-900/30`}>
                  <v.icon className={`h-6 w-6 text-${v.color}-600 dark:text-${v.color}-400`} />
                </div>
                <h3 className="font-bold text-slate-900 dark:text-white mb-2">{v.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── NEW: Our Journey ── */}
        <div className="mb-12">
          <div className="text-center mb-12">
            <span className="inline-block bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Our Journey</span>
            <h2 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] mb-4">From a small team to India's trusted DSA</h2>
          </div>
          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-emerald-200 dark:bg-emerald-900/50 hidden md:block" />
            <div className="space-y-8">
              {milestones.map((m, i) => (
                <div key={i} className="flex gap-8 items-start group">
                  <div className="flex flex-col items-center shrink-0">
                    <div className="h-16 w-16 rounded-2xl bg-white dark:bg-slate-800 border-2 border-emerald-200 dark:border-emerald-800 flex items-center justify-center font-black text-emerald-600 dark:text-emerald-400 text-sm shadow-sm group-hover:border-emerald-500 group-hover:bg-emerald-50 dark:group-hover:bg-emerald-900/30 transition-all">
                      {m.year}
                    </div>
                  </div>
                  <div className="flex-1 bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 p-5 hover:shadow-md transition-shadow">
                    <p className="text-slate-700 dark:text-slate-300 leading-relaxed">{m.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default About;
